import { useEffect, useState } from 'react';
import {
  getWeeklyReportVersionModuleRankSave,
  getWeeklyVersionList,
} from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { gameInfoType } from '@op/types/common';
import { notification } from 'tea-component';
import _, { debounce } from 'lodash-es';
import { useStateRef } from '@op/hooks/common';
import useLanguage from '@op/hooks/useLanguage';

interface Props {
  gameInfo: gameInfoType;
}

const joinFlag = '#_#';

export type OnDragEndFunc = (
  records: Record<string, any>[],
  dragContext?: {
    dragKey?: string;
    dragRecordIndex?: number;
    dragRecord?: Record<string, any>;
    dropKey?: string;
    dropDepth?: number;
  },
) => void;

function getId(item: any, parentId?: string) {
  if (parentId) {
    return `${parentId}${joinFlag}${item.id}`;
  }
  return `${item.id}`;
}

function splitId(id: string) {
  const [versionId, moduleId] = id.split(joinFlag);
  return { versionId, moduleId };
}

export function useTableDrag({
  gameInfo,
  setRecords,
  records,
  originalSortObj,
  setOriginalSortObj,
}: {
  gameInfo: gameInfoType;
  setRecords: any;
  records: React.MutableRefObject<any>;
  originalSortObj: React.MutableRefObject<Record<string, number[]>>;
  setOriginalSortObj: (obj: Record<string, number[]>) => void;
}) {
  const { formatMessage } = useLanguage();
  // 提交当前排序状态
  const submitCurrentSort = debounce((recordList: any[] = records.current) => {
    const currentSortObj: Record<string, number[]> = {
      ...originalSortObj.current,
    };
    // 遍历所有父级（版本）及其子级（模块）
    const updates = recordList.map(parent => {
      const { id, childrenList = [] } = parent;
      const list: Array<number> = [];
      childrenList.forEach((child: any) => {
        const { id: moduleId } = child;
        list.push(moduleId);
      });

      if (_.isEqual(list, originalSortObj.current[id])) {
        return null;
      }

      currentSortObj[id] = list;

      return {
        version_id: id,
        list: list.map((id, index) => ({ rank: index, id })),
      };
    });

    // 更新新的排序对象
    setOriginalSortObj(currentSortObj);

    // 过滤掉没有子级的版本
    const validUpdates = updates.filter(
      update => update && update.list.length > 0,
    );

    if (validUpdates.length === 0) return Promise.resolve();
    // 批量提交排序结果
    return Promise.all(
      validUpdates.map(update =>
        getWeeklyReportVersionModuleRankSave({
          data: { ...gameInfo, ...update },
        }),
      ),
    ).then(() => {});
  }, 1500);

  const onDragEnd: OnDragEndFunc = (_records, dragContext) => {
    // console.log('records', records, dragContext);

    const { dragKey, dropKey } = dragContext || {};
    if (!dragKey || !dropKey) return;

    const { versionId: dragVersionId } = splitId(dragKey);
    const { versionId: dropVersionId } = splitId(dropKey);

    // 2. 验证拖拽合法性：versionId 必须相同
    if (dragVersionId !== dropVersionId) {
      notification.error({
        description: formatMessage(
          'The versions are different, so dragging and dropping is not possible.',
        ),
      });
      return;
    }

    // 3. 原始数据
    const newRecords = JSON.parse(JSON.stringify(records.current));

    // 4. 直接使用 dragRecord 中的父级数据，无需遍历查找
    const parentIndex = newRecords.findIndex(
      (record: any) => record.tableId === dragVersionId,
    );

    if (parentIndex === -1) {
      console.log('未找到父级记录');
      return;
    }

    const parent = newRecords[parentIndex];
    if (!parent.childrenList) {
      console.log('父级记录无子级数据');
      return;
    }

    const { childrenList, tableId } = parent;

    const dragItem = childrenList.find(
      (child: any) => child.tableId === dragKey,
    );

    const newChildrenList: Array<any> = dropKey === tableId ? [dragItem] : [];

    // dropKey: 目标位置后面放下， dragKey: 当前拖拽的
    childrenList.forEach((child: any) => {
      if (child.tableId !== dragKey) {
        newChildrenList.push(child);
      }

      if (child.tableId === dropKey) {
        newChildrenList.push(dragItem);
      }
    });

    // 4. 更新父级数据
    parent.childrenList = newChildrenList;

    setRecords(newRecords);
    submitCurrentSort();
  };

  return { onDragEnd, submitCurrentSort };
}

export default function useTableData({ gameInfo }: Props) {
  const [recordRef, records, setRecords] = useStateRef<any>([]);
  // 当前展开
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [originalSortObjRef, _originalSortObj, setOriginalSortObj] =
    useStateRef<Record<string, number[]>>({});
  const [draggableRowKeys, setDraggableRowKeys] = useState<Array<string>>([]);
  const { wrapperCancelRequest, loading } = useCancelRequestHook();
  const { submitCurrentSort, onDragEnd } = useTableDrag({
    gameInfo,
    setRecords,
    records: recordRef,
    originalSortObj: originalSortObjRef as any,
    setOriginalSortObj,
  });

  function getRecordsData() {
    wrapperCancelRequest(getWeeklyVersionList, {
      ...gameInfo,
      gameid: gameInfo.edition_unified_id,
      order_bys: [{ field: 'updated_at', sort: 'DESC' }],
    })
      .then(data => {
        const draggableRowKeys: Array<string> = [];
        const originalSortObj: any = {};
        const recordsData = (data || []).map((item: any) => {
          const { id, modules, ...itemConfig } = item;
          if (!originalSortObj[id]) {
            originalSortObj[id] = [];
          }
          const muduleList = (modules || []).map((it: any) => {
            const key = getId(it, id);
            draggableRowKeys.push(key);
            originalSortObj[id].push(it.id);
            return { ...it, tableId: key };
          });
          return {
            ...itemConfig,
            childrenList: muduleList,
            tableId: getId(item),
            id,
          };
        });
        setOriginalSortObj(originalSortObj);
        setDraggableRowKeys(draggableRowKeys);
        setRecords(recordsData);
        setExpandedKeys(recordsData.map((item: any) => item.tableId));
      })
      .catch(e => {
        console.log(e);
      });
  }

  const handleSortEffectiveTime = (type?: 'desc' | 'asc') => {
    if (!type) {
      getRecordsData(); // 重新获取最新数据
      return;
    }

    const sortRecords = records.sort((a: any, b: any) => {
      const dateA = new Date(a.effective_end_time);
      const dateB = new Date(b.effective_end_time);

      if (type === 'desc') {
        return dateA.getTime() - dateB.getTime();
      }
      return dateB.getTime() - dateA.getTime();
    });

    setRecords(sortRecords);
  };

  useEffect(() => {
    getRecordsData();
  }, []);

  return {
    records,
    getRecordsData,
    loading,
    draggableRowKeys,
    submitCurrentSort,
    onDragEnd,
    handleSortEffectiveTime,
    expandedKeys,
    setExpandedKeys,
  };
}
